import React, { useCallback, useContext, useMemo, useState } from 'react'

import HttpStatus from 'components/Common/HttpStatus'
import Error404Page from 'components/Pages/Error404Page'
import shouldShowGiftCards from 'lib/payment/shouldShowGiftCards'
import { cartInitialiseGiftCard } from 'actions/CartActions'
import config from 'constants/config'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import HeadData from 'components/Common/HeadData'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Spacing from 'components/Common/Spacing'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Faq from 'components/Common/Faq'
import IconedCallout from 'components/Common/IconedCallout'
import Select from 'components/Common/Form/Input/Select'

import LoggedInOnly from 'components/Common/LoggedInOnly'
import ImageHero from 'components/Common/ImageHero'
import GiftCardIcon from 'components/Luxkit/Icons/custom/GiftCardIcon'
import PrintGiftCardIcon from 'components/Luxkit/Icons/custom/PrintGiftCardIcon'
import MoneyIcon from 'components/Luxkit/Icons/custom/MoneyIcon'
import { last } from 'lib/array/arrayUtils'
import { formatDynamicAmount } from 'lib/format/formatCurrencyIntl'
import TextButton from 'components/Luxkit/Button/TextButton'
import TextLink from 'components/Luxkit/TextLink'
import { connect } from 'react-redux'

import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useAppDispatch } from 'hooks/reduxHooks'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { generateGiftCardItem } from 'lib/checkout/gift/cart'
import TextInput from 'components/Common/Form/Input/TextInput'
import { formToObject } from 'lib/forms/formToObject'
import { addToCart, openRedeemGiftCardEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import ModalContext from 'contexts/ModalContext'
import RedeemGiftCard from 'components/Common/RedeemGiftCard/RedeemGiftCard'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'

const BuyPaneBody = styled(PaneBody)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(80)};
    padding-bottom: ${rem(80)};
  }
`

const SpacedSubtitle = styled(Subtitle)`
  margin-top: ${rem(32)};
`

const SameHeightPaneSm = styled(Pane)`
  margin-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    margin-top: 0;
    height: 100%;
  }
`

const ThreeColumnGrid = styled.div`
  display: grid;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const FormContainer = styled.div`
  display: grid;
  gap: ${rem(32)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 3fr 2fr;
  }
`

interface Props {
  amountOptions: Array<number>;
  currency: string;
  regionCode: string;
}

interface FormData {
  giftCardOtherValue?: string;
  giftCardValue: string;
}

function GiftCardsPage(props: Props) {
  const { regionCode, currency, amountOptions } = props
  const [otherAmount, setOtherAmount] = useState(false)
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)

  const onProceedToCheckout = useCallback((e) => {
    e.preventDefault()
    const giftCardData = formToObject<FormData>(e.currentTarget)
    const selectedAmount = parseInt(giftCardData.giftCardOtherValue ?? giftCardData.giftCardValue)
    const giftCardItem = generateGiftCardItem(selectedAmount)
    dispatch(addToCheckoutCart([giftCardItem]))
    Analytics.track('add-to-cart', addToCart())
    dispatch(navigateToCheckout())
  }, [dispatch])

  const howItWorksItems = useMemo(() => [
    {
      title: 'Purchase Online ',
      text: `Simply choose your Gift Card value, from ${amountOptions[0]} up to ${last(amountOptions)}, and select the Buy Now button.`,
      icon: <GiftCardIcon size={68} />,
    },
    {
      title: 'Receive by Email',
      text: 'The Gift Card will be sent by email for you to print or forward to the lucky recipient.',
      icon: <PrintGiftCardIcon size={68} />,
    },
    {
      title: 'The World is Their Oyster',
      text: 'The recipient enters their unique code at checkout and their account will be credited with the purchased value, valid for three years against the holiday of their choice.',
      icon: <MoneyIcon size={68} />,
    },
  ], [amountOptions])

  const onAmountSelect = useCallback((e: React.FormEvent<HTMLSelectElement>) => {
    if (e.currentTarget.value === '-1') {
      return setOtherAmount(true)
    }

    setOtherAmount(false)
  }, [])

  const openGiftCardModal = useCallback(() => {
    Analytics.track('client-event', openRedeemGiftCardEvent(AnalyticsPage.giftCards))
    showModal(
      <AnalyticsPageContext.Provider value={AnalyticsPage.giftCards}>
        <RedeemGiftCard />
      </AnalyticsPageContext.Provider>,
    )
  }, [showModal])

  if (!shouldShowGiftCards(currency) || !config.giftCards) {
    return (
      <HttpStatus code={404}>
        <Error404Page invalidRegion />
      </HttpStatus>
    )
  }

  return (
    <>
      <HeadData
        title={`${config.title} Gift Cards & Vouchers`}
        description={`Treat them to something they really want with a ${config.title} Gift Card. From local weekends away to once-in-a-lifetime getaways in exotic locales, they’ll have the world at their fingertips with great deals to amazing places.`}
      />
      <Pane axis="vertical">
        <BuyPaneBody size="md">
          <LayoutContainer>
            <FormContainer>
              <ResponsiveImage id={config.giftCards.imageId} />
              <form onSubmit={onProceedToCheckout}>
                <VerticalSpacer gap={24}>
                  <Heading variant="heading2" as="h1">{config.title} Gift Card</Heading>
                  <BodyText variant="large" as="p">Treat them to something they really want with a {config.title} Gift Card. From local weekends away to once-in-a-lifetime getaways in exotic locales, they’ll have the world at their fingertips with great deals to amazing places.</BodyText>
                  <LoggedInOnly
                    buttonText="Log in to buy or redeem"
                    buttonProps={{ fit: 'flex' }}
                  >
                    <VerticalSpacer gap={8}>
                      <Select
                        id="gift-card-value"
                        label="Select card value"
                        name="giftCardValue"
                        onChange={onAmountSelect}
                        defaultValue={amountOptions[0]}
                        data-testid="gift-card-value-select"
                        required
                      >
                        {amountOptions.map((amount) =>
                          <option key={amount} value={amount}>
                            {formatDynamicAmount(amount, currency, regionCode)}
                          </option>)
                        }
                        <option value="-1">Other amount</option>
                      </Select>
                      {otherAmount && (
                        <TextInput
                          label="Enter your amount"
                          placeholder={`Min ${formatDynamicAmount(amountOptions[0], currency, regionCode)} - Max ${formatDynamicAmount(last(amountOptions), currency, regionCode)}`}
                          required
                          type="number"
                          name="giftCardOtherValue"
                          min={amountOptions[0]}
                          max={last(amountOptions)}
                        />
                      )}
                      <TextButton kind="primary" fit="full-width" type="submit" data-testid="gift-card-add-to-cart">
                        Buy now
                      </TextButton>
                    </VerticalSpacer>
                    <VerticalSpacer gap={8}>
                      <Heading variant="heading6">
                        Ready to redeem a gift card?
                      </Heading>
                      <TextButton kind="secondary" fit="full-width" onClick={openGiftCardModal}>
                        Redeem a gift card
                      </TextButton>
                    </VerticalSpacer>
                  </LoggedInOnly>
                </VerticalSpacer>
              </form>
            </FormContainer>
          </LayoutContainer>
        </BuyPaneBody>
      </Pane>
      <ImageHero
        alt="Priceless Memories Included"
        id={config.giftCards.bannerImageId}
      >
        <LayoutContainer size="sm">
          <Heading variant="heading2" align="center" colour="neutral-eight" as="h2">
            Priceless Memories Included
          </Heading>
          <SpacedSubtitle variant="subtitle1" align="center" colour="neutral-eight" forwardedAs="h2">
            Whether you’re buying for someone special, a friend, family member, or even a customer, client or employee, the {config.title} Gift Card is a present everyone is guaranteed to love. Best of all, they can enjoy the ultimate flexibility of three years’ validity to book whichever escape they choose.
          </SpacedSubtitle>
        </LayoutContainer>
      </ImageHero>
      <Pane type="light">
        <PaneBody size="xl-y md">
          <LayoutContainer>
            <Heading variant="heading3" align="center" as="h2">How It Works</Heading>
            <PaneBody size="xl-y">
              <ThreeColumnGrid>
                {howItWorksItems.map((item, i) =>
                  <SameHeightPaneSm key={i}>
                    <PaneBody size="lg xl-y">
                      <IconedCallout
                        icon={item.icon}
                        title={item.title}
                        text={item.text}
                      />
                    </PaneBody>
                  </SameHeightPaneSm>,
                )}
              </ThreeColumnGrid>
            </PaneBody>
            <BodyText variant="large" align="center">Read our full{' '}
              {config.giftCards.externalTermsAndConditions ?
                <TextLink weight="regular" href={config.giftCards.externalTermsAndConditions}>Terms &amp; Conditions</TextLink> :
                <TextLink weight="regular" to="/giftcards-terms">Terms &amp; Conditions</TextLink>
              }
            </BodyText>
          </LayoutContainer>
        </PaneBody>
      </Pane>
      <Pane>
        <PaneBody size="xl-y md">
          <LayoutContainer>
            <Heading variant="heading3" align="center" as="h2">Frequently Asked Questions</Heading>
            <Spacing mt={40}>
              <Faq slug={config.giftCards.faqSlug} />
            </Spacing>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  currency: state.geo.currentCurrency,
  amountOptions: state.giftCard.giftCardOptions,
  regionCode: state.geo.currentRegionCode,
})

const mapDispatchToProps = {
  cartInitialiseGiftCard,
}

export default connect(mapStateToProps, mapDispatchToProps)(GiftCardsPage)
